'use client'

import { useState } from 'react'
import Header from '@/components/header'
import {
  ArrowLeft,
  Globe,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileText,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  ImageRun,
  HeadingLevel,
} from 'docx'
import { saveAs } from 'file-saver'
import Api from '@/api'

interface ImageData {
  url: string
  buffer: Uint8Array
  type: 'jpg' | 'png' | 'gif' | 'bmp'
  base64?: string
}

type OutputFormat = 'docx' | 'image'

export default function WebToDocxPage() {
  const router = useRouter()
  const [url, setUrl] = useState('https://www.qq.com')
  const [isProcessing, setIsProcessing] = useState(false)
  const [status, setStatus] = useState<
    'idle' | 'fetching' | 'converting' | 'success' | 'error'
  >('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [progress, setProgress] = useState(0)
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('image')

  // 处理URL输入
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value)
    setStatus('idle')
    setErrorMessage('')
  }

  // 验证URL
  const isValidUrl = (urlString: string) => {
    try {
      const url = new URL(urlString)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }

  // 获取网页内容并转换
  const handleConvert = async () => {
    if (!url.trim()) {
      setErrorMessage('请输入网址')
      setStatus('error')
      return
    }

    if (!isValidUrl(url)) {
      setErrorMessage('请输入有效的网址（需以 http:// 或 https:// 开头）')
      setStatus('error')
      return
    }

    setIsProcessing(true)
    setStatus('fetching')
    setProgress(10)
    setErrorMessage('')

    if (outputFormat === 'image') {
      try {
        // 步骤1: 获取网页真实尺寸
        setProgress(10)
        setStatus('fetching')

        const sizeRes: any = await Api.h5.chromiumGetPageSize({
          url: url,
        })

        if (sizeRes.code !== 200) {
          throw new Error(sizeRes.message || '获取页面尺寸失败')
        }

        const { width, height } = sizeRes.data
        console.log(`页面尺寸: ${width} x ${height}`)

        // 步骤2: 使用真实尺寸调用后端截图接口
        setProgress(30)
        setStatus('converting')

        const res: any = await Api.h5.chromiumSearch({
          type: 'selenium-webdriver',
          url: url,
          width: width,
          height: height,
        })

        if (res.code === 200) {
          setProgress(70)

          const screenshot = res.data.screenshot

          // 将纯base64转换为完整的data URL
          const imgData = `data:image/png;base64,${screenshot}`

          setProgress(80)

          // 直接下载图片
          const link = document.createElement('a')
          link.href = imgData
          link.download = `screenshot_${Date.now()}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          setProgress(100)
          setStatus('success')

          setTimeout(() => {
            setStatus('idle')
            setProgress(0)
            setUrl('')
          }, 3000)
        } else {
          throw new Error(res.message || '截图失败')
        }
      } catch (error: any) {
        console.error('转换失败:', error)
        setErrorMessage(error.message || '转换失败，请检查网址是否正确')
        setStatus('error')
      } finally {
        setIsProcessing(false)
      }
    } else if (outputFormat === 'docx') {
      try {
        // 步骤1: 获取网页内容
        setProgress(20)
        Api.h5
          .toolsGetUrlContent({
            url: url,
          })
          .then(async (res: any) => {
            if (res.code === 200) {
              const html = await res.data.html

              // 步骤2: 解析HTML
              setStatus('converting')
              setProgress(60)
              const parser = new DOMParser()
              const doc = parser.parseFromString(html, 'text/html')

              // 移除脚本和样式标签
              doc
                .querySelectorAll('script, style, nav, footer, header')
                .forEach((el) => el.remove())

              // 获取标题
              const title =
                doc.querySelector('title')?.textContent || 'Web Content'

              // 获取主要内容
              const body = doc.body

              // 提取文本内容和图片
              const { paragraphs, images } = await extractContent(body, url)

              setProgress(80)

              // 步骤3: 下载图片
              const imageData = await downloadImages(images)

              setProgress(90)

              // 步骤4: 生成Word文档
              await generateWordDocument(title, paragraphs, imageData)

              setProgress(100)
              setStatus('success')

              setTimeout(() => {
                setStatus('idle')
                setProgress(0)
                setUrl('')
              }, 3000)
            }
          })
      } catch (error: any) {
        console.error('转换失败:', error)
        setErrorMessage(error.message || '转换失败，请检查网址是否正确')
        setStatus('error')
      } finally {
        setIsProcessing(false)
      }
    }
  }

  // 提取内容
  const extractContent = async (
    element: HTMLElement,
    baseUrl: string
  ): Promise<{
    paragraphs: Array<{
      text: string
      type: 'heading' | 'paragraph' | 'image'
      level?: number
      imageUrl?: string
    }>
    images: string[]
  }> => {
    const paragraphs: Array<{
      text: string
      type: 'heading' | 'paragraph' | 'image'
      level?: number
      imageUrl?: string
    }> = []
    const images: string[] = []

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim()
        if (text && text.length > 0) {
          paragraphs.push({ text, type: 'paragraph' })
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement

        // 处理标题
        if (/^H[1-6]$/.test(el.tagName)) {
          const level = parseInt(el.tagName[1])
          const text = el.textContent?.trim()
          if (text) {
            paragraphs.push({ text, type: 'heading', level })
          }
        }
        // 处理段落
        else if (el.tagName === 'P') {
          const text = el.textContent?.trim()
          if (text) {
            paragraphs.push({ text, type: 'paragraph' })
          }
        }
        // 处理图片
        else if (el.tagName === 'IMG') {
          const src = el.getAttribute('src')
          if (src) {
            try {
              const imageUrl = new URL(src, baseUrl).href
              images.push(imageUrl)
              paragraphs.push({ text: '', type: 'image', imageUrl })
            } catch (e) {
              console.warn('Invalid image URL:', src)
            }
          }
        }
        // 处理列表
        else if (el.tagName === 'LI') {
          const text = el.textContent?.trim()
          if (text) {
            paragraphs.push({ text: `• ${text}`, type: 'paragraph' })
          }
        }
        // 递归处理子节点
        else {
          el.childNodes.forEach(processNode)
        }
      }
    }

    element.childNodes.forEach(processNode)

    return { paragraphs, images }
  }

  function convertBase64UrlToBlob(urlData: any) {
    if (typeof window === 'undefined') {
      throw new Error('This function can only be called in the browser')
    }
    let bytes = window.atob(urlData.split(',')[1])
    let ab = new ArrayBuffer(bytes.length)
    let ia = new Uint8Array(ab)
    for (var i = 0; i < bytes.length; i++) {
      ia[i] = bytes.charCodeAt(i)
    }

    return new Blob([ab], {
      type: 'image/jpg',
    })
  }

  // 下载图片
  const downloadImages = async (imageUrls: string[]): Promise<ImageData[]> => {
    const imageData: ImageData[] = []

    for (const imageUrl of imageUrls.slice(0, 20)) {
      try {
        let res: any = await Api.h5.toolsGetUrlContent({
          url: imageUrl,
        })

        if (res.code === 200) {
          let base64 = res.data.base64
          let blob = convertBase64UrlToBlob(base64)
          const arrayBuffer = await blob.arrayBuffer()
          const buffer = new Uint8Array(arrayBuffer)

          // 判断图片类型
          let type: 'jpg' | 'png' | 'gif' | 'bmp' = 'png'
          if (blob.type.includes('jpeg') || blob.type.includes('jpg')) {
            type = 'jpg'
          } else if (blob.type.includes('png')) {
            type = 'png'
          } else if (blob.type.includes('gif')) {
            type = 'gif'
          } else if (blob.type.includes('bmp')) {
            type = 'bmp'
          }

          imageData.push({ url: imageUrl, buffer, type, base64 })
        }
      } catch (error) {
        console.warn('Failed to download image:', imageUrl, error)
      }
    }

    return imageData
  }

  // 生成Word文档
  const generateWordDocument = async (
    title: string,
    paragraphs: Array<{
      text: string
      type: 'heading' | 'paragraph' | 'image'
      level?: number
      imageUrl?: string
    }>,
    imageData: ImageData[]
  ) => {
    const children: Paragraph[] = []

    // 添加标题
    children.push(
      new Paragraph({
        text: title,
        heading: HeadingLevel.TITLE,
        spacing: { after: 400 },
      })
    )

    // 创建图片URL到数据的映射
    const imageMap = new Map<string, ImageData>()
    imageData.forEach((img) => imageMap.set(img.url, img))

    // 添加内容
    for (const para of paragraphs) {
      if (para.type === 'heading') {
        const headingLevel =
          para.level === 1
            ? HeadingLevel.HEADING_1
            : para.level === 2
              ? HeadingLevel.HEADING_2
              : para.level === 3
                ? HeadingLevel.HEADING_3
                : para.level === 4
                  ? HeadingLevel.HEADING_4
                  : para.level === 5
                    ? HeadingLevel.HEADING_5
                    : HeadingLevel.HEADING_6

        children.push(
          new Paragraph({
            text: para.text,
            heading: headingLevel,
            spacing: { before: 240, after: 120 },
          })
        )
      } else if (para.type === 'image' && para.imageUrl) {
        const imgData = imageMap.get(para.imageUrl)
        if (imgData) {
          try {
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    type: imgData.type,
                    data: imgData.buffer,
                    transformation: {
                      width: 500,
                      height: 400,
                    },
                  }),
                ],
                spacing: { before: 120, after: 120 },
              })
            )
          } catch (error) {
            console.warn('Failed to add image to document:', error)
          }
        }
      } else if (para.type === 'paragraph' && para.text) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: para.text,
                font: 'Microsoft YaHei',
              }),
            ],
            spacing: { after: 120 },
          })
        )
      }
    }

    // 创建文档
    const doc = new Document({
      sections: [
        {
          properties: {},
          children,
        },
      ],
    })

    // 生成并下载
    const blob = await Packer.toBlob(doc)
    const fileName = `${title.replace(/[^\w\s]/g, '_')}_${Date.now()}.docx`
    saveAs(blob, fileName)
  }


  // 按键处理
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isProcessing) {
      handleConvert()
    }
  }

  return (
    <>
      <Header />

      <main className='min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden'>
        {/* 背景装饰 */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute top-20 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse-slow' />
          <div
            className='absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '2s' }}
          />
          <div
            className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-pulse-slow'
            style={{ animationDelay: '4s' }}
          />
        </div>

        {/* 内容区域 */}
        <div className='relative max-w-4xl mx-auto px-4 py-12'>
          {/* 返回按钮 */}
          <button
            onClick={() => router.push('/light')}
            className='group mb-8 flex items-center gap-2 px-4 py-3 rounded-2xl bg-card/80 backdrop-blur-xl border-2 border-border hover:border-primary shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in'
          >
            <div className='relative'>
              <div className='absolute inset-0 bg-primary/20 rounded-full blur-md scale-0 group-hover:scale-150 transition-transform duration-500' />
              <ArrowLeft className='relative w-5 h-5 text-primary group-hover:text-primary transition-all duration-300 group-hover:-translate-x-1' />
            </div>
            <span className='text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-300'>
              返回
            </span>
          </button>

          {/* 主卡片 */}
          <div className='bg-card/80 backdrop-blur-xl rounded-3xl border-2 border-border shadow-2xl p-8 animate-fade-in-up'>
            {/* 标题区域 */}
            <div className='text-center mb-8'>
              <div className='inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary mb-4 shadow-lg'>
                <FileText className='w-8 h-8 text-primary-foreground' />
              </div>
              <h1 className='text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2'>
                网页转文档
              </h1>
              <p className='text-muted-foreground'>
                输入网址，一键将网页内容转换为网页截图或 Word 文档
              </p>
            </div>

            {/* 输入区域 */}
            <div className='space-y-4'>
              <div className='relative'>
                <div className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground'>
                  <Globe className='w-5 h-5' />
                </div>
                <input
                  type='text'
                  value={url}
                  onChange={handleUrlChange}
                  onKeyDown={handleKeyDown}
                  placeholder='请输入网址，例如：https://example.com'
                  disabled={isProcessing}
                  className='w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-border bg-background/50 backdrop-blur-sm
                           focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/20
                           transition-all duration-300 text-foreground placeholder:text-muted-foreground
                           disabled:opacity-50 disabled:cursor-not-allowed'
                />
              </div>

              {/* 格式选择器 */}
              <div className='flex gap-3'>
                <button
                  onClick={() => setOutputFormat('image')}
                  disabled={isProcessing}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300
                    ${
                      outputFormat === 'image'
                        ? 'bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  网页截图
                </button>
                <button
                  onClick={() => setOutputFormat('docx')}
                  disabled={isProcessing}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-300
                    ${
                      outputFormat === 'docx'
                        ? 'bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-lg'
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Word 文档
                </button>                
              </div>

              {/* 转换按钮 */}
              <button
                onClick={handleConvert}
                disabled={isProcessing || !url.trim()}
                className='w-full group relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-secondary p-[2px]
                         hover:shadow-xl hover:shadow-primary/25 transition-all duration-300
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none'
              >
                <div
                  className='relative bg-card/90 backdrop-blur-sm rounded-2xl px-6 py-4
                              group-hover:bg-transparent transition-all duration-300'
                >
                  <div className='flex items-center justify-center gap-2 text-foreground group-hover:text-primary-foreground transition-colors duration-300'>
                    {isProcessing ? (
                      <>
                        <Loader2 className='w-5 h-5 animate-spin' />
                        <span className='font-medium'>处理中...</span>
                      </>
                    ) : (
                      <>
                        <Download className='w-5 h-5' />
                        <span className='font-medium'>
                          开始转换为 {outputFormat === 'docx' ? 'Word' : '图片'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            </div>

            {/* 进度显示 */}
            {isProcessing && (
              <div className='mt-6 space-y-2 animate-fade-in'>
                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>
                    {status === 'fetching' && '正在获取网页内容...'}
                    {status === 'converting' &&
                      `正在转换为 ${outputFormat === 'docx' ? 'Word 文档' : '图片'}...`}
                  </span>
                  <span className='font-medium text-primary'>{progress}%</span>
                </div>
                <div className='h-2 bg-secondary/20 rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500 ease-out'
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            {/* 状态消息 */}
            {status === 'success' && (
              <div className='mt-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20 animate-fade-in'>
                <CheckCircle2 className='w-5 h-5 text-green-500 flex-shrink-0' />
                <p className='text-sm text-green-600 dark:text-green-400'>
                  转换成功！{outputFormat === 'docx' ? 'Word 文档' : '图片'}已开始下载
                </p>
              </div>
            )}

            {status === 'error' && errorMessage && (
              <div className='mt-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/10 border border-destructive/20 animate-fade-in'>
                <AlertCircle className='w-5 h-5 text-destructive flex-shrink-0' />
                <p className='text-sm text-destructive'>{errorMessage}</p>
              </div>
            )}

            {/* 使用说明 */}
            <div className='mt-8 p-6 rounded-2xl bg-muted/50 backdrop-blur-sm border border-border/50'>
              <h3 className='text-sm font-semibold text-foreground mb-3 flex items-center gap-2'>
                <div className='w-1.5 h-1.5 rounded-full bg-primary animate-pulse' />
                使用说明
              </h3>
              <ul className='space-y-2 text-sm text-muted-foreground'>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>输入完整的网址（需包含 http:// 或 https://）</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>选择输出格式：Word 文档(.docx) 或 网页截图(.png)</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>支持提取网页文本、标题、图片等内容</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>最多支持 20 张图片，图片会保持原有格式</span>
                </li>
                <li className='flex items-start gap-2'>
                  <span className='text-primary mt-0.5'>•</span>
                  <span>使用服务器代理解决跨域问题</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
